import type { VideoContentObject } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps } from 'ui';
import { useUpdateStatusMutation } from '../../../../queries';
import { useReportVideo } from './useReportVideo';

export const useActions = ({
  onRequestEdit,
  videoContentObject,
}: {
  onRequestEdit: (opened: true) => void;
  videoContentObject: VideoContentObject;
}) => {
  const { t } = useTranslation();
  const { confirm: reportVideo, contextHolder } = useReportVideo({ videoContentObject });
  const { mutate: updateStatus } = useUpdateStatusMutation();
  const { canArchiveVideo, canEditVideo } = usePermissions();

  return useMemo(() => {
    const actions: MenuProps['items'] = [];

    if (canEditVideo({ video: videoContentObject })) {
      actions.push({
        icon: <Icon icon="pencil-line" />,
        key: 'edit',
        label: t('Edit'),
        onClick() {
          onRequestEdit(true);
        },
      });
    }

    actions.push({
      icon: <Icon icon="flag-1-line" />,
      key: 'report',
      label: t('Report'),
      onClick() {
        reportVideo();
      },
    });

    if (canArchiveVideo({ video: videoContentObject })) {
      actions.push({
        icon: <Icon icon="archive-line" />,
        key: 'archive',
        label: t('Archive'),
        onClick() {
          updateStatus({ id: videoContentObject.video.id, video: { status: 'archived' } });
        },
      });
    }

    return {
      actions,
      contextHolder,
    };
  }, [canEditVideo, videoContentObject, t, canArchiveVideo, contextHolder, onRequestEdit, reportVideo, updateStatus]);
};
